import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { QueryKey } from '@tanstack/react-query'
import { useFarmContext } from 'src/Elements/Farm'
import { useTypedSelector } from 'src/State/RootReducer'
import { ConnectedWallbox, getWallboxesByStorage, getWallboxesByStorageMaster } from '../api/wallboxes.api'
import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult, usePortalQuery } from 'src/Libs/api'
import { AuthFetchError } from '@e3dc-react/shell/Types'

type UseConnectedWallboxesQuery = {
  data: ConnectedWallbox[] | null
  isLoading?: boolean
  isError?: boolean
}

type WallboxQueryFn = (
  serialNo: string | undefined,
  authFetch: AuthFetchWithResponse
) => Promise<ApiResult<ConnectedWallbox[], AuthFetchError | Error>>

export const getConnectedWallboxesQueryKey = (storageId: string | undefined): QueryKey => ['connected-wallboxes', storageId]

const useConnectedWallboxesQuery = (enabled = true): UseConnectedWallboxesQuery => {
  const storageId = useTypedSelector(state => state.storage.activeStorage)
  const authFetch = useAuthFetchWithResponse()
  const { isFarmMasterSelected, isLoading: isFarmContextLoading } = useFarmContext()

  const queryFn: WallboxQueryFn | undefined = (() => {
    if (isFarmContextLoading) return undefined
    if (isFarmMasterSelected) return getWallboxesByStorageMaster
    return getWallboxesByStorage
  })()

  const { data, isLoading, isError } = usePortalQuery({
    queryKey: getConnectedWallboxesQueryKey(storageId),
    queryFn: async () => (queryFn ? queryFn(storageId, authFetch) : new ApiResult<ConnectedWallbox[]>([])),
    select: data => data ?? null,
    enabled: Boolean(storageId) && enabled && !isFarmContextLoading,
  })

  return { data: data || null, isLoading, isError }
}

export default useConnectedWallboxesQuery
